/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Hyunsu Sim (hssim@rsupport.com)
 *
 * @description
 *
 * @created at Mon Oct 07 2024
 **/

import { useCallback, useMemo, useState } from "react";
import { useTheme } from "@emotion/react";
import { isEmpty } from "lodash-es";
import cx from "classnames";
import parse from "html-react-parser";

import { Button, Icon, Row, Typography } from "@/frameworks/designStuff/components";
import { ChronoUtils, Configure } from "@/frameworks/libraries/utilities";
import { _T } from "@rfice/language";
import { Choice, Maybe } from "@/frameworks/libraries/components";

import VoteMore from "./VoteMore";
import * as S from "../../emotion";
import { useChat } from "@/frameworks/core/API";
import { usePopover } from "@/frameworks/core/hooks";
import { POPOVER_ID } from "@/frameworks/core/commons/PopoverID";
import { ZIndex } from "@/frameworks/core/commons";
import { useDMRoomState, useSpaceRoomState } from "@/frameworks/core/chat";
import { MessageList } from "@/frameworks/core/chat";

export type VoteType = "create" | "end" | "remind";

type Vote = {
	vote: MessageList[string]["vote"];
	roomID: string;
	spaceID?: string;
	voteType: VoteType;
	isSearch?: boolean;
};

type VoteState = {
	allowAnonymous: boolean;
	allowMultipleChoice: boolean;
	list: string[];
};

export default function Vote({ vote, roomID, spaceID, voteType, isSearch = false }: Vote) {
	if (!vote) return null;
	const chatAPI = useChat();
	const popover = usePopover();
	const { colors } = useTheme();

	const spaceRoom = useSpaceRoomState({ spaceID: spaceID ?? "", roomID });
	const dmRoom = useDMRoomState(roomID);

	const channelData = spaceRoom ?? dmRoom;
	const votedList = vote.items.filter((item) => item.votedByMe).map((item) => item.voteItemId);

	const isShowResult = vote.end || votedList.length > 0;

	const maxCount = useMemo(() => Math.max(...vote.items.map((item) => item.count)), [vote.items]);
	const voteItems = useMemo(() => {
		return vote.items.map((item) => ({
			...item,
			isHighest: item.count === maxCount
		}));
	}, [vote.items]);

	const [voteState, setVoteState] = useState<VoteState>({
		allowAnonymous: vote.allowAnonymous,
		allowMultipleChoice: vote.allowMultipleChoice,
		list: vote.items.filter((item) => item.votedByMe).map((item) => item.voteItemId)
	});

	const choicesHandler = useCallback(
		(selectedVoteID: string) => {
			let choices;
			if (vote.allowMultipleChoice) {
				if (votedList.length > 0) {
					choices = [
						{
							voteItemId: selectedVoteID,
							choice: isEmpty(votedList.find((voteID) => voteID === selectedVoteID))
						}
					];
				} else {
					choices = [
						{
							voteItemId: selectedVoteID,
							choice: true
						}
					];
				}
			} else {
				if (votedList.length > 0) {
					if (isEmpty(votedList.find((voteID) => voteID === selectedVoteID))) {
						choices = votedList
							.map((votedID) => {
								return {
									voteItemId: votedID,
									choice: false
								};
							})
							.concat({
								voteItemId: selectedVoteID,
								choice: true
							});
					} else {
						choices = votedList.map((votedID) => {
							return {
								voteItemId: votedID === selectedVoteID ? selectedVoteID : votedID,
								choice: Boolean(votedID !== selectedVoteID)
							};
						});
					}
				} else {
					choices = [
						{
							voteItemId: selectedVoteID,
							choice: true
						}
					];
				}
			}

			return choices;
		},
		[votedList, vote]
	);

	const handleClick = useCallback(
		async (voteItemID: string) => {
			await chatAPI.vote.choose.execute({
				roomId: roomID,
				voteId: vote.voteId,
				choices: choicesHandler(voteItemID)
			});

			setVoteState((prev) => {
				if (!voteState.allowMultipleChoice) {
					return {
						...prev,
						list: selectedChecker(votedList, voteItemID) ? [] : [voteItemID]
					};
				}
				return {
					...prev,
					list: selectedChecker(votedList, voteItemID)
						? prev.list.filter((id) => id !== voteItemID)
						: prev.list.concat(voteItemID)
				};
			});
		},
		[vote]
	);

	const handleMore = (ev: React.MouseEvent<HTMLButtonElement>) => {
		const target = ev.currentTarget as HTMLButtonElement;

		popover.show({
			popoverID: POPOVER_ID.VOTE_MORE,
			content: <VoteMore roomID={roomID} voteID={vote.voteId} />,
			anchorEl: target,
			direction: {
				x: "right",
				y: "top"
			},
			position: {
				x: 0,
				y: 0
			},
			zIndex: ZIndex.CHAT_POPUP
		});
	};

	return (
		<S.VoteWrapper>
			<S.TitleWrapper>
				<Typography.p typoSize="Body2" typoColor="neutral_70_100">
					<Choice value={voteType}>
						<Choice.Case test="create">{_T("투표가 시작되었습니다.")}</Choice.Case>
						<Choice.Case test="end">{_T("투표가 종료되었습니다.")}</Choice.Case>
						<Choice.Case test="remind">{_T("투표종료 30분전입니다.")}</Choice.Case>
					</Choice>
					<Maybe test={isSearch}> {parse(vote.title)}</Maybe>
				</Typography.p>
			</S.TitleWrapper>
			<Maybe
				test={isSearch}
				fallback={
					<S.VoteContent>
						<S.VoteBox>
							<S.VoteHeader horizonAlign="distribute">
								<Row verticalAlign="center" gap={10}>
									<Icon name="Vote24" />
									<Typography.p typoSize="Title3_B" typoColor="neutral_80_100">
										{parse(vote.title)}
									</Typography.p>
								</Row>
								<Maybe test={vote.createdBy === Configure.userID}>
									<Button.Icon
										name="More24"
										size="small_18"
										onClick={handleMore}
										filled="transparent"
									/>
								</Maybe>
							</S.VoteHeader>
							<Maybe
								test={vote.end}
								fallback={
									<S.VoteListBox>
										{vote.items.map((item) => (
											<S.VoteRenderItem
												key={item.voteItemId}
												className={cx({
													selected: item.votedByMe
												})}
												onClick={() => handleClick(item.voteItemId)}
											>
												<Typography typoSize="Body3" typoColor="neutral_80_100">
													{item.content}
												</Typography>
											</S.VoteRenderItem>
										))}
									</S.VoteListBox>
								}
							/>
							<S.CountWrapper
								verticalAlign="center"
								horizonAlign={vote.allowMultipleChoice || vote.allowAnonymous ? "distribute" : "right"}
							>
								<Maybe test={vote.allowMultipleChoice && !vote.allowAnonymous}>
									<Typography.span typoSize="Body5" typoColor="neutral_60_100">
										{_T("복수 선택 가능")}
									</Typography.span>
								</Maybe>
								<Maybe test={vote.allowAnonymous && !vote.allowMultipleChoice}>
									<Typography.span typoSize="Body5" typoColor="neutral_60_100">
										{_T("익명 투표")}
									</Typography.span>
								</Maybe>
								<Maybe test={vote.allowAnonymous && vote.allowMultipleChoice}>
									<Typography.span typoSize="Body5" typoColor="neutral_60_100">
										{_T("복수 선택 가능, 익명 투표")}
									</Typography.span>
								</Maybe>
								<Typography.span typoSize="Body5" typoColor="neutral_60_100">
									{_T("{{count}}명 참여", { count: vote.voterCount || 0 })}
								</Typography.span>
							</S.CountWrapper>
						</S.VoteBox>

						<Maybe test={isShowResult}>
							<S.VoteResultContent>
								{voteItems.map((item) => (
									<S.VoteResultItem key={item.voteItemId}>
										<Typography.p typoSize="Body3" typoColor="neutral_80_100">
											{item.content}
										</Typography.p>
										<S.GraphArea gap={2} verticalAlign="center">
											<S.GraphBarWrapper>
												<S.GraphBar
													isHighest={item.isHighest}
													count={item.count}
													totalCount={channelData?.users.length || 0}
												/>
											</S.GraphBarWrapper>
											<Typography typoSize="Body6" typoColor="neutral_80_100">
												{String(item.count)}
											</Typography>
										</S.GraphArea>
										<Maybe
											test={vote.allowAnonymous}
											fallback={
												<S.VoterTitle typoSize="Body6" typoColor="neutral_80_100">
													{item.voters.map((item) => item.name).join(", ")}
												</S.VoterTitle>
											}
										/>
									</S.VoteResultItem>
								))}
							</S.VoteResultContent>
						</Maybe>
						<S.DeadlineWrapper gap={2} verticalAlign="center" horizonAlign="left">
							<Icon name="Clock24" size={16} fill={colors.neutral_60_100} />
							<Typography typoSize="Body5" typoColor="neutral_60_100">
								{ChronoUtils.formatDateByYear(vote.deadline)}{" "}
								{ChronoUtils.timestampFollowCountry(vote.deadline)} {_T("종료")}
							</Typography>
						</S.DeadlineWrapper>
					</S.VoteContent>
				}
			/>
		</S.VoteWrapper>
	);
}

const selectedChecker = (list: string[], itemID: string) => {
	return !isEmpty(list.find((voteItemID) => voteItemID === itemID));
};
